import React, { Component } from 'react';
import Select, { ActionMeta, InputActionMeta, MultiValue, PropsValue, SingleValue } from 'react-select';
import { FormFieldFunctions } from '../../interfaces/FormField/FormField';
import { GlobalContext } from '../../interfaces/GlobalContext/GlobalContext';
import Option from '../../interfaces/Option/Option';
import Snapshot from '../../interfaces/Snapshot/Snapshot';
import { CustomDropdownIndicator, CustomInput } from '../FormFieldSelect/FormFieldSelectPartials';
import { formFieldSelectStylesForCustomerDataPage } from '../FormFieldSelect/FormFieldSelectStyles';
import FormFieldSelectCityProps from './FormFieldSelectCity.props';
import FormFieldSelectCityState from './FormFieldSelectCity.state';

export default class FormFieldSelectCity extends Component<FormFieldSelectCityProps, FormFieldSelectCityState> implements FormFieldFunctions {
    static contextType = GlobalContext;
    context!: React.ContextType<typeof GlobalContext>;
    private selectElement: React.RefObject<HTMLDivElement>;

    constructor(props: FormFieldSelectCityProps) {
        super(props);
        this.state = {
            selected: this.props.selected,
            options: this.props.selected
                ? [{
                    label: this.props.selected,
                    value: this.props.selected
                }]
                : [],
            plz: this.props.plz
        };

        this.selectElement = React.createRef<HTMLDivElement>();
        this.handleSelectChange = this.handleSelectChange.bind(this);
        this.updateCityWithPLZFromState = this.updateCityWithPLZFromState.bind(this);
        this.onBlur = this.onBlur.bind(this);
        this.isValid = this.isValid.bind(this);
        this.toggleValidationError = this.toggleValidationError.bind(this);
    }

    handleSelectChange(newValue: MultiValue<Option> | SingleValue<Option>, actionMeta: ActionMeta<Option>) {
        this.setState({
            selected: (newValue as Option).value,
        });
    }

    onBlur() {
        if (this.isValid()) {
            this.toggleValidationError(false);
            if (this.props.formFieldStreet?.state.city !== this.state.selected
                || this.props.formFieldStreet?.state.plz !== this.state.plz) {
                this.props.formFieldStreet?.resetField();
                this.props.formFieldStreet?.setState({
                    plz: this.state.plz,
                    city: this.state.selected
                }, this.props.formFieldStreet.getFocus);
            }
        }
    }

    resetField() {
        this.setState({
            options: [],
            plz: undefined,
            selected: undefined,
        });
        this.props.formFieldStreet?.resetField();
    }

    updateAndNotify = () => {
        this.setState({
            selected: this.props.selected,
            options: this.props.selected
                ? [{
                    label: this.props.selected,
                    value: this.props.selected
                }]
                : [],
            plz: this.props.plz
        });
    }

    getSnapshotBeforeUpdate(prevProps: FormFieldSelectCityProps): Snapshot {
        return { notifyRequired: prevProps.selected !== this.props.selected || prevProps.plz !== this.props.plz };
    }

    componentDidUpdate(prevProps: FormFieldSelectCityProps, prevState: FormFieldSelectCityState, snapshot: Snapshot) {
        if (snapshot.notifyRequired) {
            this.updateAndNotify();
        }
    }

    async updateCityWithPLZFromState() {
        if (this.state.plz === undefined || this.state.plz.trim() === '') {
            this.setState({
                options: []
            });
        }

        const response = await this.context.requestProvider.postData(
            '/jportal/wsrest/recherche3/addressSuggestCity',
            {
                portalId: 'jurisw',
                plz: this.state.plz,
                cityPrefix: '', // has to be empty!
                r3ID: (new Date()).toISOString(),
            }
        );

        if (response && response.status === 200) {
            const newCityList = (await response.json()).list || [];
            const upgradedCityList = newCityList.map((city: string) => {
                return {
                    label: city,
                    value: city
                } as Option
            }) as Option[];
            // console.log(newCityList);
            // console.log(upgradedCityList);
            this.setState({
                selected: newCityList[0],
                options: upgradedCityList,
            }, () => {
                this.onBlur();
            });
        }
    }

    isValid() {
        if (this.props.required && (this.state.selected === undefined || this.state.selected.trim() === '')) {
            return false;
        }

        return true;
    }

    toggleValidationError(show: boolean) {
        if (show) {
            this.selectElement.current?.parentElement?.classList.add('form__field--error');
        } else {
            this.selectElement.current?.parentElement?.classList.remove('form__field--error');
        }
    }

    getValue() {
        return this.state.selected;
    }

    render(): React.ReactNode {
        return (
            <div data-testid="FormFieldSelectCity"
                className={
                    "select"
                    + (!!this.state.selected && this.state.selected !== 'default' ? " select--option-selected" : "")
                }
                ref={this.selectElement}>
                <div className="select__inner">
                    <div className="select__content">
                        {
                            this.props.label &&
                            <label className="select__label" htmlFor={this.props.id}>{this.props.label}</label>
                        }
                        <Select inputId={this.props.id}
                            tabIndex={undefined}
                            tabSelectsValue={false}
                            maxLength={this.props.maxLength}
                            name={this.props.name || this.props.id}
                            value={(this.state.selected && {
                                label: this.state.selected,
                                value: this.state.selected
                            } as PropsValue<Option>) || undefined}
                            onInputChange={(newValue: string, actionMeta: InputActionMeta) => {
                                // console.log(actionMeta)
                                if (actionMeta.action === 'input-change') {
                                    this.setState({
                                        selected: newValue
                                    });
                                }
                            }}
                            inputValue={this.state.selected}
                            options={this.state.options}
                            placeholder={this.props.label}
                            // defaultMenuIsOpen={true} // for debug purposes
                            noOptionsMessage={() => "Keine Optionen"}
                            isDisabled={
                                (this.props.isLoggedIn && this.state.options.length >= 1)
                                || (!this.props.isLoggedIn && this.state.options.length <= 1)
                            }
                            closeMenuOnSelect={true}
                            isMulti={false}
                            onChange={this.handleSelectChange}
                            onBlur={this.onBlur}
                            styles={formFieldSelectStylesForCustomerDataPage}
                            components={{
                                DropdownIndicator: CustomDropdownIndicator,
                                Input: CustomInput,
                            }} />
                    </div>
                </div>
            </div>
        );
    }
}
